import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import {
  GET_SELECTED_WORK_ORDER_OPERATION,
  GET_WORK_ORDER_OPERATIONS,
} from 'lib/api/queries';
import { getIsAPMEnabled } from 'lib/selectors/getIsAPMEnabled';
import { getByPartsWhere } from '../helpers';

/**
 * Change-job / split-change-job flows lead with OTHER work orders for the SAME
 * part operation as the run being changed, before the operator expands into the
 * full search. Given the run's workOrderOperationRef, this resolves its part
 * operation and fetches every open work order operation that shares it
 * (shop-wide, excluding the run's own WOO). Reuses `getByPartsWhere`, the
 * existing same-part-operation where-clause builder.
 *
 * @param {object} params
 * @param {number|string|null} params.workOrderOperationRef - the run being changed
 * @param {boolean} params.skip - skip both queries (e.g. not a change-job flow)
 */
const useSamePartOperationWorkOrderOps = ({
  workOrderOperationRef,
  skip = false,
} = {}) => {
  const isAPMEnabled = useSelector(getIsAPMEnabled);
  const skipAll = skip || workOrderOperationRef == null;

  // Step 1 — resolve the run's part operation from its WOO.
  const { data: selectedData, loading: selectedLoading } = useQuery(
    GET_SELECTED_WORK_ORDER_OPERATION,
    {
      variables: { workOrderOperationRef },
      skip: skipAll,
      fetchPolicy: 'cache-first',
    }
  );

  const orderOperation =
    selectedData?.erpWorkOrderOperation?.matchingPartOperations?.[0] ?? null;
  const partOperationRef = orderOperation?.partOperationRef ?? null;

  // Step 2 — fetch all open WOOs for that part operation.
  const where = useMemo(() => {
    if (partOperationRef == null) return null;
    return getByPartsWhere({ isAPMEnabled, orderOperation });
  }, [partOperationRef, isAPMEnabled, orderOperation]);

  const { data: opsData, loading: opsLoading } = useQuery(
    GET_WORK_ORDER_OPERATIONS,
    {
      variables: { where, limit: 100 },
      skip: skipAll || where == null,
      fetchPolicy: 'cache-and-network',
    }
  );

  const samePartOperationWorkOrderOps = useMemo(() => {
    const ops = opsData?.erpWorkOrderOperations ?? [];
    // The run's own WOO is surfaced separately as the "Current job" card.
    const runRef = String(workOrderOperationRef);
    return ops.filter((op) => {
      return String(op.workOrderOperationRef) !== runRef;
    });
  }, [opsData, workOrderOperationRef]);

  return {
    samePartOperationWorkOrderOps,
    partOperationRef,
    orderOperation,
    loading: selectedLoading || opsLoading,
  };
};

export default useSamePartOperationWorkOrderOps;
